/**
 * Indonesian glossary and lesson copy.
 *
 * Written for someone who has never played Mahjong: every term is explained,
 * sentences stay short, and each lesson says what to do in one line.
 */

import type { LocaleContent } from "./content.js";

export const ID_CONTENT: LocaleContent = {
  glossary: [
    { id: "tile", term: "Tile", meaning: "Satu kartu Mahjong. Satu set berisi 136 tile." },
    { id: "hand", term: "Tangan", meaning: "Tile yang Anda pegang dan hanya Anda yang bisa melihatnya." },
    { id: "wall", term: "Dinding", meaning: "Tumpukan tile yang belum diambil. Jika habis, ronde berakhir seri." },
    { id: "draw", term: "Ambil", meaning: "Mengambil satu tile dari dinding. Setiap giliran dimulai dengan ini." },
    { id: "discard", term: "Buang", meaning: "Meletakkan satu tile dari tangan ke meja. Semua pemain bisa melihatnya." },
    { id: "pair", term: "Pasangan", meaning: "Dua tile yang sama persis. Tangan menang butuh tepat satu pasangan." },
    { id: "chow", term: "Chow", meaning: "Tiga tile berurutan dari jenis yang sama, misalnya 3-4-5 Bambu. Hanya boleh diambil dari buangan pemain di kiri Anda." },
    { id: "pong", term: "Pong", meaning: "Tiga tile yang sama. Boleh diambil dari buangan pemain mana pun." },
    { id: "kong", term: "Kong", meaning: "Empat tile yang sama. Setelah Kong Anda mengambil satu tile pengganti." },
    { id: "meld", term: "Set", meaning: "Tiga tile yang sudah lengkap: chow atau pong. Tangan menang butuh 4 set." },
    { id: "self-draw", term: "Menang dari tile sendiri", meaning: "Menang dengan tile yang Anda ambil sendiri dari dinding. Sering disebut tsumo." },
    { id: "win-from-discard", term: "Menang dari buangan", meaning: "Menang dengan tile yang baru dibuang pemain lain. Sering disebut ron." },
    { id: "dealer", term: "Dealer", meaning: "Pemain yang memulai ronde. Dealer memegang 14 tile dan langsung membuang satu." },
    { id: "seat-wind", term: "Angin kursi", meaning: "Arah kursi Anda: Timur, Selatan, Barat atau Utara. Giliran berjalan sesuai urutan itu." },
  ],
  lessons: {
    dasar: {
      title: "1. Dasar Mahjong",
      objective: "Mengenal tujuan permainan dan isi satu set Mahjong.",
      intro: [
        "Mahjong dimainkan 4 orang dengan 136 tile.",
        "Tujuan dasarnya adalah membentuk 4 set dan 1 pasangan.",
        "Set adalah tiga tile: tiga tile yang sama (Pong) atau tiga tile berurutan dari jenis yang sama (Chow). Pasangan adalah dua tile yang sama.",
        "Ada tiga jenis bernomor 1-9, yaitu Karakter, Bambu dan Lingkaran, ditambah 4 Angin dan 3 Naga.",
        "Anda adalah dealer dan memegang 14 tile, jadi langkah pertama Anda adalah membuang satu tile.",
      ],
      goal: "Buang satu tile dari tangan Anda.",
      coach: "Cari tile yang paling sendirian, yaitu yang tidak punya pasangan maupun tetangga. Tile seperti itu paling kecil peluangnya menjadi set.",
      success: "Bagus. Itu satu giliran penuh: ambil tile, lalu buang satu.",
    },
    "ambil-buang": {
      title: "2. Ambil & Buang",
      objective: "Menguasai ritme setiap giliran: ambil satu tile, buang satu tile.",
      intro: [
        "Setiap giliran selalu sama: ambil satu tile dari dinding, lalu buang satu tile.",
        "Jumlah tile di tangan Anda karena itu selalu kembali ke 13.",
        "Tile yang baru diambil ditandai terpisah supaya mudah dikenali.",
        "Giliran berpindah searah: Anda, lalu Selatan, Barat, Utara.",
      ],
      goal: "Ambil satu tile dari dinding, lalu buang satu tile.",
      coach: "Tekan Ambil tile lebih dulu. Setelah tile baru masuk, pilih satu tile yang paling kecil gunanya untuk dibuang.",
      success: "Itu ritme dasar Mahjong. Sekarang kita lihat cara mengambil buangan lawan.",
    },
    chow: {
      title: "3. Chow",
      objective: "Melengkapi urutan dari tile yang dibuang pemain di kiri Anda.",
      intro: [
        "Chow adalah tiga tile berurutan dari jenis yang sama, misalnya 3-4-5 Bambu.",
        "Anda boleh mengambil buangan untuk melengkapi urutan, tetapi hanya dari pemain di kiri Anda, yaitu pemain yang bergerak tepat sebelum Anda.",
        "Anda memegang 3 dan 4 Bambu. Pemain Utara membuang 5 Bambu, dan itu melengkapi 3-4-5.",
        "Setelah Chow, set itu terbuka di meja dan Anda langsung membuang satu tile.",
      ],
      goal: "Ambil buangan 5 Bambu untuk membentuk urutan 3-4-5.",
      coach: "Tekan tombol Chow di panel klaim. Set yang terbentuk akan muncul terbuka di bawah tangan Anda.",
      success: "Chow berhasil. Perhatikan set terbuka di bawah tangan Anda.",
    },
    pong: {
      title: "4. Pong",
      objective: "Melengkapi tiga tile yang sama dari buangan pemain mana pun.",
      intro: [
        "Pong adalah tiga tile yang sama.",
        "Berbeda dengan Chow, Pong boleh diambil dari buangan pemain mana pun di meja.",
        "Anda memegang dua Naga Merah, dan pemain Selatan baru saja membuang Naga Merah ketiga.",
        "Kalau dua pemain mengklaim tile yang sama, Pong menang atas Chow.",
      ],
      goal: "Ambil buangan Naga Merah untuk membentuk tiga tile sama.",
      coach: "Tekan tombol Pong. Setelah itu Anda tetap harus membuang satu tile, karena klaim tidak menambah giliran.",
      success: "Pong berhasil. Set tiga Naga Merah sekarang terbuka di meja.",
    },
    kong: {
      title: "5. Kong",
      objective: "Membentuk empat tile sama dan memahami tile pengganti.",
      intro: [
        "Kong adalah empat tile yang sama.",
        "Anda bisa membentuknya dari buangan lawan, atau dari tangan sendiri kalau keempatnya sudah ada.",
        "Kong tetap dihitung sebagai satu set saja.",
        "Karena itu Anda langsung mengambil satu tile pengganti dari dinding cadangan supaya jumlah tile di tangan tetap benar.",
      ],
      goal: "Ambil buangan Angin Barat keempat untuk membentuk Kong.",
      coach: "Tekan tombol Kong. Tile pengganti akan otomatis masuk ke tangan Anda, lalu buang satu tile.",
      success: "Kong berhasil, dan Anda sudah menerima tile pengganti. Sekarang buang satu tile.",
    },
    "menang-sendiri": {
      title: "6. Menang dari Tile Sendiri",
      objective: "Menutup tangan menjadi 4 set + 1 pasangan dengan tile dari dinding.",
      intro: [
        "Tangan Anda tinggal butuh satu Naga Merah untuk menjadi 4 set dan 1 pasangan.",
        "Tile berikutnya di dinding adalah Naga Merah.",
        "Ambil tile itu, lalu tekan tombol Menang.",
        "Menang dengan tile yang Anda ambil sendiri disebut menang dari tile sendiri, atau tsumo.",
      ],
      goal: "Ambil tile dari dinding, lalu umumkan menang.",
      coach: "Tekan Ambil tile dulu. Begitu Naga Merah masuk, tombol Menang akan aktif.",
      success: "Selamat, itu kemenangan pertama Anda!",
    },
    "menang-dari-buangan": {
      title: "7. Menang dari Buangan",
      objective: "Menutup tangan dengan tile yang baru dibuang pemain lain.",
      intro: [
        "Anda juga bisa menang dari tile yang dibuang pemain lain. Itu disebut ron.",
        "Pemain Barat baru saja membuang Naga Merah yang Anda tunggu.",
        "Klaim menang mengalahkan semua klaim lain, termasuk Pong dan Chow.",
      ],
      goal: "Klaim buangan Naga Merah untuk menang.",
      coach: "Tekan tombol Menang! di panel klaim, bukan Lewati. Buangan itu melengkapi tangan Anda.",
      success: "Sempurna. Sekarang Anda tahu dua cara menang: dari dinding dan dari buangan.",
    },
  },
};
